import { defineRelations } from "drizzle-orm";

import { tables } from "./tables";

export const relations = defineRelations(tables, (r) => ({
  playerProfiles: {
    user: r.one.users({
      from: r.playerProfiles.userId,
      to: r.users.id,
    }),
    level: r.one.levels({
      from: r.playerProfiles.levelId,
      to: r.levels.id,
    }),
    memberships: r.many.memberships(),
    activeMembership: r.one.memberships({
      where: {
        validUntil: {
          gte: "now()",
        },
        revokedAt: {
          isNull: true,
        },
      },
    }),
    userProfiles: r.many.userProfiles(),
    legacyUsers: r.many.legacyUsers({
      from: r.playerProfiles.id,
      to: r.legacyUsers.playerProfileId,
    }),
    invites: r.many.tournamentInvites(),
  },
  memberships: {
    profile: r.one.playerProfiles({
      from: r.memberships.profileId,
      to: r.playerProfiles.id,
      optional: false,
    }),
    user: r.one.users({
      from: r.memberships.profileId.through(r.playerProfiles.id),
      to: r.users.id.through(r.playerProfiles.userId),
      optional: false,
    }),
    invoice: r.one.invoices({
      from: r.memberships.invoiceId,
      to: r.invoices.id,
      optional: false,
    }),
  },
  invoices: {
    purchaser: r.one.users({
      from: r.invoices.purchaserId,
      to: r.users.id,
      optional: false,
    }),
    memberships: r.many.memberships(),
    tournamentRegistrations: r.many.tournamentRegistrations(),
    teamRegistrations: r.many.tournamentDivisionTeams({
      from: r.invoices.id,
      to: r.tournamentDivisionTeams.invoiceId,
    }),
    refunds: r.many.refunds(),
  },
  refunds: {
    invoice: r.one.invoices({
      from: r.refunds.invoiceId,
      to: r.invoices.id,
      optional: false,
    }),
    tournamentDivisionTeam: r.one.tournamentDivisionTeams({
      from: r.refunds.tournamentDivisionTeamId,
      to: r.tournamentDivisionTeams.id,
    }),
    membership: r.one.memberships({
      from: r.refunds.membershipId,
      to: r.memberships.id,
    }),
  },
  tournamentRegistrations: {
    invoice: r.one.invoices({
      from: r.tournamentRegistrations.invoiceId,
      to: r.invoices.id,
      optional: false,
    }),
    tournamentDivisionTeam: r.one.tournamentDivisionTeams({
      from: r.tournamentRegistrations.tournamentDivisionTeamId,
      to: r.tournamentDivisionTeams.id,
      optional: false,
    }),
  },
  pools: {
    tournamentDivision: r.one.tournamentDivisions({
      from: r.pools.tournamentDivisionId,
      to: r.tournamentDivisions.id,
      optional: false,
    }),
    teams: r.many.poolTeams(),
    // tournamentDivisionTeams: r.many.tournamentDivisionTeams({
    // 	from: r.pools.id.through(r.poolTeams.poolId),
    // 	to: r.tournamentDivisionTeams.id.through(r.poolTeams.teamId),
    // }),
    matches: r.many.poolMatches(),
  },
  poolTeams: {
    pool: r.one.pools({
      from: r.poolTeams.poolId,
      to: r.pools.id,
      optional: false,
    }),
    team: r.one.tournamentDivisionTeams({
      from: r.poolTeams.teamId,
      to: r.tournamentDivisionTeams.id,
      optional: false,
    }),
  },
  poolMatches: {
    sets: r.many.matchSets(),
    pool: r.one.pools({
      from: r.poolMatches.poolId,
      to: r.pools.id,
      optional: false,
    }),
    teamA: r.one.tournamentDivisionTeams({
      from: r.poolMatches.teamAId,
      to: r.tournamentDivisionTeams.id,
    }),
    teamB: r.one.tournamentDivisionTeams({
      from: r.poolMatches.teamBId,
      to: r.tournamentDivisionTeams.id,
    }),
    refs: r.many.matchRefs({
      from: r.poolMatches.id,
      to: r.matchRefs.poolMatchId,
    }),
  },
  playoffMatches: {
    sets: r.many.matchSets(),
    nextMatch: r.one.playoffMatches({
      from: r.playoffMatches.nextMatchId,
      to: r.playoffMatches.id,
    }),
    teamA: r.one.tournamentDivisionTeams({
      from: r.playoffMatches.teamAId,
      to: r.tournamentDivisionTeams.id,
    }),
    teamB: r.one.tournamentDivisionTeams({
      from: r.playoffMatches.teamBId,
      to: r.tournamentDivisionTeams.id,
    }),
    refs: r.many.matchRefs({
      from: r.playoffMatches.id,
      to: r.matchRefs.playoffMatchId,
    }),
    tournamentDivision: r.one.tournamentDivisions({
      from: r.playoffMatches.tournamentDivisionId,
      to: r.tournamentDivisions.id,
      optional: false,
    }),
  },
  matchSets: {
    poolMatch: r.one.poolMatches({
      from: r.matchSets.poolMatchId,
      to: r.poolMatches.id,
    }),
    playoffMatch: r.one.playoffMatches({
      from: r.matchSets.playoffMatchId,
      to: r.playoffMatches.id,
    }),
    scoreEvents: r.many.scoreEvents(),
  },
  scoreEvents: {
    matchSet: r.one.matchSets({
      from: r.scoreEvents.matchSetId,
      to: r.matchSets.id,
      optional: false,
    }),
    creator: r.one.users({
      from: r.scoreEvents.createdBy,
      to: r.users.id,
    }),
  },
  matchRefs: {
    poolMatch: r.one.poolMatches({
      from: r.matchRefs.poolMatchId,
      to: r.poolMatches.id,
    }),
    playoffMatch: r.one.playoffMatches({
      from: r.matchRefs.playoffMatchId,
      to: r.playoffMatches.id,
    }),
    profile: r.one.playerProfiles({
      from: r.matchRefs.profileId,
      to: r.playerProfiles.id,
      optional: false,
    }),
    user: r.one.users({
      from: r.matchRefs.profileId.through(r.playerProfiles.id),
      to: r.users.id.through(r.playerProfiles.userId),
      optional: false,
    }),
    team: r.one.tournamentDivisionTeams({
      from: r.matchRefs.teamId,
      to: r.tournamentDivisionTeams.id,
    }),
  },
  teams: {
    players: r.many.teamPlayers(),
    profiles: r.many.playerProfiles({
      from: r.teams.id.through(r.teamPlayers.teamId),
      to: r.playerProfiles.id.through(r.teamPlayers.playerProfileId),
    }),
  },
  teamPlayers: {
    profile: r.one.playerProfiles({
      from: r.teamPlayers.playerProfileId,
      to: r.playerProfiles.id,
      optional: false,
    }),
    team: r.one.teams({
      from: r.teamPlayers.teamId,
      to: r.teams.id,
      optional: false,
    }),
    levelAtTournament: r.one.levels({
      from: r.teamPlayers.levelIdAtTournament,
      to: r.levels.id,
    }),
  },
  tournamentDivisionTeams: {
    team: r.one.teams({
      from: r.tournamentDivisionTeams.teamId,
      to: r.teams.id,
      optional: false,
    }),
    players: r.many.teamPlayers({
      from: r.tournamentDivisionTeams.teamId.through(r.teamPlayers.teamId),
      to: r.teamPlayers.id.through(r.teamPlayers.id),
    }),
    poolTeam: r.one.poolTeams({
      from: r.tournamentDivisionTeams.id,
      to: r.poolTeams.teamId,
    }),
    tournamentDivision: r.one.tournamentDivisions({
      from: r.tournamentDivisionTeams.tournamentDivisionId,
      to: r.tournamentDivisions.id,
      optional: false,
    }),
    invoice: r.one.invoices({
      from: r.tournamentDivisionTeams.invoiceId,
      to: r.invoices.id,
    }),
    levelEarned: r.one.levels({
      from: r.tournamentDivisionTeams.levelEarnedId,
      to: r.levels.id,
    }),
    transferChargeInvoice: r.one.invoices({
      from: r.tournamentDivisionTeams.transferChargeInvoiceId,
      to: r.invoices.id,
    }),
    registration: r.one.tournamentRegistrations({
      from: r.tournamentDivisionTeams.id,
      to: r.tournamentRegistrations.tournamentDivisionTeamId,
    }),
    refunds: r.many.refunds({
      from: r.tournamentDivisionTeams.id,
      to: r.refunds.tournamentDivisionTeamId,
    }),
  },
  tournaments: {
    venue: r.one.venues({
      from: r.tournaments.venueId,
      to: r.venues.id,
      optional: false,
    }),
    directors: r.many.tournamentDirectors(),
    tournamentDivisions: r.many.tournamentDivisions(),
    galleryPhotos: r.many.tournamentGalleryPhotos(),
    inviteSources: r.many.tournamentInviteSources({
      from: r.tournaments.id,
      to: r.tournamentInviteSources.targetTournamentId,
    }),
    invites: r.many.tournamentInvites({
      from: r.tournaments.id,
      to: r.tournamentInvites.targetTournamentId,
    }),
  },
  tournamentGalleryPhotos: {
    tournament: r.one.tournaments({
      from: r.tournamentGalleryPhotos.tournamentId,
      to: r.tournaments.id,
      optional: false,
    }),
  },
  tournamentDirectors: {
    tournament: r.one.tournaments({
      from: r.tournamentDirectors.tournamentId,
      to: r.tournaments.id,
    }),
    director: r.one.directors({
      from: r.tournamentDirectors.directorId,
      to: r.directors.id,
      optional: false,
    }),
  },
  tournamentDivisions: {
    tournament: r.one.tournaments({
      from: r.tournamentDivisions.tournamentId,
      to: r.tournaments.id,
      optional: false,
    }),
    division: r.one.divisions({
      from: r.tournamentDivisions.divisionId,
      to: r.divisions.id,
      optional: false,
    }),
    sanctionedAs: r.one.divisions({
      from: r.tournamentDivisions.sanctionedAsId,
      to: r.divisions.id,
    }),
    pools: r.many.pools(),
    teams: r.many.tournamentDivisionTeams(),
    requirements: r.many.tournamentDivisionRequirements(),
    checkIns: r.many.tournamentPlayerCheckIns(),
    playoffBracketTemplate: r.one.playoffBracketTemplates({
      from: r.tournamentDivisions.playoffBracketTemplateId,
      to: r.playoffBracketTemplates.id,
    }),
  },
  tournamentPlayerCheckIns: {
    tournamentDivision: r.one.tournamentDivisions({
      from: r.tournamentPlayerCheckIns.tournamentDivisionId,
      to: r.tournamentDivisions.id,
      optional: false,
    }),
    playerProfile: r.one.playerProfiles({
      from: r.tournamentPlayerCheckIns.playerProfileId,
      to: r.playerProfiles.id,
      optional: false,
    }),
    checkedInByUser: r.one.users({
      from: r.tournamentPlayerCheckIns.checkedInBy,
      to: r.users.id,
    }),
  },
  venues: {
    tournaments: r.many.tournaments(),
    director: r.one.users({
      from: r.venues.directorId,
      to: r.users.id,
    }),
    directors: r.many.venueDirectors(),
    defaultBracketTemplate: r.one.playoffBracketTemplates({
      from: r.venues.defaultBracketTemplateId,
      to: r.playoffBracketTemplates.id,
    }),
  },
  playoffBracketTemplates: {
    venue: r.one.venues({
      from: r.playoffBracketTemplates.venueId,
      to: r.venues.id,
      optional: false,
    }),
    divisions: r.many.tournamentDivisions({
      from: r.playoffBracketTemplates.id,
      to: r.tournamentDivisions.playoffBracketTemplateId,
    }),
  },
  venueDirectors: {
    venue: r.one.venues({
      from: r.venueDirectors.venueId,
      to: r.venues.id,
      optional: false,
    }),
    director: r.one.directors({
      from: r.venueDirectors.directorId,
      to: r.directors.id,
      optional: false,
    }),
  },
  paymentIntents: {
    user: r.one.users({
      from: r.paymentIntents.userId,
      to: r.users.id,
      optional: false,
    }),
    invoice: r.one.invoices({
      from: r.paymentIntents.invoiceId,
      to: r.invoices.id,
    }),
  },
  users: {
    invoices: r.many.invoices(),
    paymentIntents: r.many.paymentIntents(),
    profiles: r.many.playerProfiles(),
    userProfiles: r.many.userProfiles(),
    selfProfile: r.one.userProfiles({
      where: { role: "self" },
    }),
    sessions: r.many.sessions({
      from: r.users.id,
      to: r.sessions.userId,
    }),
    claimedLegacyUsers: r.many.legacyUsers({
      from: r.users.id,
      to: r.legacyUsers.claimedByUserId,
    }),
  },
  directors: {
    profile: r.one.playerProfiles({
      from: r.directors.profileId,
      to: r.playerProfiles.id,
      optional: false,
    }),
  },
  tournamentDivisionRequirements: {
    tournamentDivision: r.one.tournamentDivisions({
      from: r.tournamentDivisionRequirements.tournamentDivisionId,
      to: r.tournamentDivisions.id,
      optional: false,
    }),
    qualifiedDivision: r.one.divisions({
      from: r.tournamentDivisionRequirements.qualifiedDivisionId,
      to: r.divisions.id,
    }),
  },
  userProfiles: {
    user: r.one.users({
      from: r.userProfiles.userId,
      to: r.users.id,
      optional: false,
    }),
    playerProfile: r.one.playerProfiles({
      from: r.userProfiles.playerProfileId,
      to: r.playerProfiles.id,
      optional: false,
    }),
  },
  legacyUsers: {
    playerProfile: r.one.playerProfiles({
      from: r.legacyUsers.playerProfileId,
      to: r.playerProfiles.id,
    }),
    claimedBy: r.one.users({
      from: r.legacyUsers.claimedByUserId,
      to: r.users.id,
    }),
  },
  tournamentInviteSources: {
    sourceTournamentDivision: r.one.tournamentDivisions({
      from: r.tournamentInviteSources.sourceTournamentDivisionId,
      to: r.tournamentDivisions.id,
      optional: false,
    }),
    targetTournament: r.one.tournaments({
      from: r.tournamentInviteSources.targetTournamentId,
      to: r.tournaments.id,
      optional: false,
    }),
    invites: r.many.tournamentInvites({
      from: r.tournamentInviteSources.id,
      to: r.tournamentInvites.sourceId,
    }),
  },
  tournamentInvites: {
    playerProfile: r.one.playerProfiles({
      from: r.tournamentInvites.playerProfileId,
      to: r.playerProfiles.id,
      optional: false,
    }),
    targetTournament: r.one.tournaments({
      from: r.tournamentInvites.targetTournamentId,
      to: r.tournaments.id,
      optional: false,
    }),
    source: r.one.tournamentInviteSources({
      from: r.tournamentInvites.sourceId,
      to: r.tournamentInviteSources.id,
    }),
  },
}));
